/** Small formatting helpers for data-dense UI. */

export function relativeTime(iso: string, now: number = Date.now()): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return iso
  const diff = Math.round((now - then) / 1000)
  const abs = Math.abs(diff)
  const suffix = diff >= 0 ? 'ago' : 'from now'
  if (abs < 60) return `${abs}s ${suffix}`
  if (abs < 3600) return `${Math.floor(abs / 60)}m ${suffix}`
  if (abs < 86400) return `${Math.floor(abs / 3600)}h ${suffix}`
  return `${Math.floor(abs / 86400)}d ${suffix}`
}

const pad2 = (n: number) => String(n).padStart(2, '0')

/** HH:MM:SS local time for event feeds. */
export function timeOfDay(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`
}

/** "2026-08-06" in the viewer's LOCAL time zone (same clock as `timeOfDay`),
 *  with invalid-date fallback to the input. */
export function localDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

/** "2026-08-06 14:22" in the viewer's LOCAL time zone, with invalid-date fallback. */
export function shortDateTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return `${localDate(iso)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`
}

/** "2026-08-06" as the UTC calendar date. ONLY for calendar-date values the
 *  backend stores at UTC midnight (door-code `valid_from`/`valid_to`: the
 *  rotation engine and the date-input forms both write 00:00Z): the stored
 *  instant IS that date, so it must not be shifted into the viewer's zone
 *  (west of UTC it would show the day before), and it round-trips into a
 *  `<input type="date">` unchanged. Never use it for an event time. */
export function utcDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toISOString().slice(0, 10)
}

/** Long-form duration since an ISO timestamp, largest two units:
 *  "2 days 20 hours", "45 minutes". */
export function humanDurationSince(iso: string, now: number = Date.now()): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return iso
  const s = Math.max(0, Math.round((now - then) / 1000))
  const days = Math.floor(s / 86400)
  const hours = Math.floor((s % 86400) / 3600)
  const minutes = Math.floor((s % 3600) / 60)
  const parts: string[] = []
  if (days > 0) parts.push(`${days} day${days === 1 ? '' : 's'}`)
  if (hours > 0) parts.push(`${hours} hour${hours === 1 ? '' : 's'}`)
  if (parts.length === 0) parts.push(`${minutes} minute${minutes === 1 ? '' : 's'}`)
  return parts.slice(0, 2).join(' ')
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
}

/** Uptime-style duration: "3d 4h", "42m", "90s". */
export function humanizeSeconds(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds))
  if (s < 60) return `${s}s`
  if (s < 3600) return `${Math.floor(s / 60)}m`
  if (s < 86400) return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`
  return `${Math.floor(s / 86400)}d ${Math.floor((s % 86400) / 3600)}h`
}

/** KiB → human size ("512 MB", "7.5 GB"). */
export function humanKb(kb: number): string {
  if (kb < 1024) return `${kb} KB`
  if (kb < 1024 * 1024) return `${Math.round(kb / 1024)} MB`
  return `${(kb / (1024 * 1024)).toFixed(1)} GB`
}

/** Bytes → human size ("1.2 MB"). */
export function humanBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}
